import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Icon, Switch, theme } from '@sg/garnish';

import { usePaymentBreakdown } from '../../../../hooks';
import { BagHsaFsaInfoIcon } from '../../BagHsaFsaInfoIcon';

export const FlexPaymentMethod = (props: FlexPaymentMethodProps) => {
  const {
    isDisabled,
    flexPaymentMethod,
    shouldUseFlex,
    amountEligibleForFlex,
    onChangeFlexUsage,
    paymentBreakdown,
  } = props;

  const { flexAmount, formattedTotalFlex } = paymentBreakdown;
  const { formatMessage } = useIntl();

  return (
    <View style={[styles.paymentMethod, styles.borderBottom]}>
      <View style={[styles.info, styles.infoText]}>
        <Icon
          name="IconFlexCard"
          color={amountEligibleForFlex ? theme.colors.SPINACH : theme.colors.GRAY}
          width={32}
          height={32}
        />

        <BodyText
          style={amountEligibleForFlex ? styles.spinachText : styles.grayText}
          sizeMatch={['14']}
        >
          {flexPaymentMethod}
        </BodyText>

        {amountEligibleForFlex ? null : <BagHsaFsaInfoIcon />}
      </View>

      <View style={styles.info}>
        <Switch
          size="small"
          disabled={isDisabled || !amountEligibleForFlex}
          accessibilityLabel={formatMessage(messages.changeHsaFsaUsage)}
          value={shouldUseFlex}
          onChange={onChangeFlexUsage}
        />

        {amountEligibleForFlex ? (
          <BodyText style={flexAmount > 0 ? styles.spinachText : undefined} sizeMatch={['14']}>
            {formattedTotalFlex}
          </BodyText>
        ) : null}
      </View>
    </View>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  changeHsaFsaUsage: {
    defaultMessage: 'Change HSA/FSA usage',
    description: 'Bag | Payment Methods | Change HSA/FSA usage',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  paymentMethod: {
    gap: theme.spacing['2'],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing['4'],
    paddingVertical: theme.spacing['3'],
  },
  borderBottom: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.LINEN,
  },
  info: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  infoText: {
    flex: 1,
    flexWrap: 'wrap',
  },
  spinachText: {
    color: theme.colors.SPINACH,
  },
  grayText: {
    color: theme.colors.GRAY,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type FlexPaymentMethodProps = {
  isDisabled: boolean;
  flexPaymentMethod: string;
  shouldUseFlex: boolean;
  amountEligibleForFlex: number | null;
  onChangeFlexUsage: (shouldUseFlex: boolean) => void;
  paymentBreakdown: ReturnType<typeof usePaymentBreakdown>;
};
